import {
  Spacer,
  getTokens,
  getVariableValue,
  styled,
  themeable,
  useMediaPropsActive,
  withStaticProperties
} from "@tamagui/core";
import { getFontSize } from "@tamagui/font-size";
import { getSpace, useGetThemedIcon } from "@tamagui/helpers-tamagui";
import { ThemeableStack, YStack } from "@tamagui/stacks";
import { SizableText, wrapChildrenInText } from "@tamagui/text";
import { forwardRef } from "react";
const NAME = "ListItem";
const ListItemFrame = styled(ThemeableStack, {
  name: NAME,
  tag: "li",
  variants: {
    unstyled: {
      false: {
        size: "$true",
        alignItems: "center",
        flexWrap: "nowrap",
        width: "100%",
        borderColor: "$borderColor",
        maxWidth: "100%",
        overflow: "hidden",
        flexDirection: "row",
        backgroundColor: "$backgroundStrong"
      }
    },
    size: {
      "...size": (val, { tokens }) => {
        return {
          minHeight: tokens.size[val],
          paddingHorizontal: tokens.space[val],
          paddingVertical: getSpace(val, -2)
        };
      }
    },
    active: {
      true: {
        hoverStyle: {
          backgroundColor: "$background"
        }
      }
    },
    disabled: {
      true: {
        opacity: 0.5,
        // TODO breaking types
        pointerEvents: "none"
      }
    }
  },
  defaultVariants: {
    unstyled: false
  }
});
const ListItemText = styled(SizableText, {
  name: "ListItemText",
  variants: {
    unstyled: {
      false: {
        color: "$color",
        userSelect: "none",
        flexGrow: 1,
        flexShrink: 1,
        ellipse: true,
        cursor: "default"
      }
    }
  },
  defaultVariants: {
    unstyled: false
  }
});
const ListItemSubtitle = styled(ListItemText, {
  name: "ListItemSubtitle",
  variants: {
    unstyled: {
      false: {
        opacity: 0.6,
        maxWidth: "100%",
        size: "$3",
        color: "$color"
      }
    }
  },
  defaultVariants: {
    unstyled: false
  }
});
const ListItemTitle = styled(ListItemText, {
  name: "ListItemTitle"
});
const useListItem = (props, {
  Text = ListItemText,
  Subtitle = ListItemSubtitle,
  Title = ListItemTitle
} = { Text: ListItemText, Subtitle: ListItemSubtitle, Title: ListItemTitle }) => {
  const {
    children,
    icon,
    iconAfter,
    noTextWrap,
    theme: themeName,
    space,
    spaceFlex,
    scaleIcon = 1,
    scaleSpace = 1,
    subTitle,
    // text props
    color,
    fontWeight,
    letterSpacing,
    fontSize,
    fontFamily,
    textAlign,
    textProps,
    title,
    ...rest
  } = props;
  const mediaActiveProps = useMediaPropsActive(props);
  const size = mediaActiveProps.size || "$true";
  const subtitleSize = `$${+String(size).replace("$", "") - 1}`;
  const iconSize = getFontSize(size) * scaleIcon;
  const getThemedIcon = useGetThemedIcon({ size: iconSize, color });
  const [themedIcon, themedIconAfter] = [icon, iconAfter].map(getThemedIcon);
  const spaceSize = getVariableValue(getTokens().space[mediaActiveProps.space] ?? iconSize) * scaleSpace;
  const contents = wrapChildrenInText(Text, mediaActiveProps);
  return {
    props: {
      fontFamily,
      ...rest,
      children: <>
        {themedIcon ? <>
          {themedIcon}
          <Spacer size={spaceSize} />
        </> : null}
        {title || subTitle ? <YStack flex={1}>
          {noTextWrap === "all" ? title : <Title size={size}>{title}</Title>}
          {subTitle ? <>{typeof subTitle === "string" && noTextWrap !== "all" ? (
            // TODO can use theme but we need to standardize to alt themes
            // or standardize on subtle colors in themes
            <Subtitle size={subtitleSize}>{subTitle}</Subtitle>
          ) : subTitle}</> : null}
          {contents}
        </YStack> : contents}
        {themedIconAfter ? <>
          <Spacer size={spaceSize} />
          {themedIconAfter}
        </> : null}
      </>
    }
  };
};
const ListItemComponent = forwardRef((props, ref) => {
  const { props: listItemProps } = useListItem(props);
  return <ListItemFrame ref={ref} justifyContent="space-between" {...listItemProps} />;
});
const listItemStaticConfig = {
  inlineProps: /* @__PURE__ */ new Set([
    // text props go here (can't really optimize them, but we never fully extract listItem anyway)
    "color",
    "fontWeight",
    "fontSize",
    "fontFamily",
    "letterSpacing",
    "textAlign"
  ])
};
const ListItem = withStaticProperties(
  ListItemFrame.extractable(
    themeable(ListItemComponent, { componentName: NAME }),
    listItemStaticConfig
  ),
  {
    Text: ListItemText,
    Subtitle: ListItemSubtitle
  }
);
export {
  ListItem,
  ListItemFrame,
  ListItemSubtitle,
  ListItemText,
  ListItemTitle,
  listItemStaticConfig,
  useListItem
};
//# sourceMappingURL=ListItem.js.map
